import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type ApiError } from "@/lib/api";
import type { RefreshStatus } from "@/types/refresh";

const POLL_MS = 2000;

export interface DatabaseRefreshResult {
  status: RefreshStatus | null;
  start: () => void;
  starting: boolean;
  startError: ApiError | Error | null;
}

export function useDatabaseRefresh(): DatabaseRefreshResult {
  const { data: status, refetch } = useQuery({
    queryKey: ["admin", "refresh-status"],
    queryFn: () => api<RefreshStatus>("/api/admin/refresh/status"),
    refetchInterval: (query) => (query.state.data?.running ? POLL_MS : false),
  });

  const start = useMutation({
    mutationFn: () => api<RefreshStatus>("/api/admin/refresh", { method: "POST" }),
    onSuccess: () => {
      void refetch();
    },
  });

  // Nothing to do here any more when a refresh finishes. This used to bump a
  // per-browser version so THIS tab would refetch, which was the whole problem:
  // it only ever helped whoever pressed the button, and every other open
  // dashboard kept serving pre-rebuild numbers for up to twelve hours with
  // nothing on screen to say so. The stamp now comes from the server, so every
  // browser notices on its own -- see src/hooks/useDataStamp.ts.

  return {
    status: status ?? null,
    start: start.mutate,
    starting: start.isPending,
    startError: (start.error as ApiError | Error | null) ?? null,
  };
}
